import { Component } from '@theme/component';

/**
 * <offsure-matching-piece>
 * Shared "complete the look" upsell row for the PDP: a slight nudge toward
 * the counterpart product in the shopper's current colorway — blank thumb,
 * name, price, and a "View" link to the counterpart PDP with that colorway
 * preselected via ?variant=. No size picker, no add-to-bag; the shopper
 * completes the purchase on the counterpart's own PDP.
 *
 * Counterpart selection is colorway-aware and computed server-side per
 * colorway of the *current* product (see snippets/offsure-matching-piece.
 * liquid): each entry in the `colors` catalog below already names the
 * correct counterpart for that color (which may differ across colors, or
 * be entirely absent). This component never re-derives pairing itself —
 * it only looks up the pre-resolved entry for whatever color the shopper
 * picks, and hides the row entirely when that color has no counterpart
 * sold in the same colorway, rather than falling back to a mismatched one.
 *
 * The host PDP's color-swatch handler calls `renderForColor(color)` on this
 * element (see offsure-pdp.liquid / offsure-pdp-v2.liquid) whenever the
 * shopper picks a different colorway.
 *
 * Progressive enhancement: without JS, the links still point at the
 * counterpart product in the color it rendered with server-side.
 */
export default class OffsureMatchingPiece extends Component {
  /** @type {{colors: Record<string, {handle:string,title:string,noun:string,url:string,price:string,thumb:string,variantId:string}>} | null} */
  #catalog = null;

  connectedCallback() {
    super.connectedCallback();

    const catalogScript = this.refs.catalog;
    if (catalogScript instanceof HTMLScriptElement) {
      try {
        this.#catalog = JSON.parse(catalogScript.textContent || 'null');
      } catch {
        this.#catalog = null;
      }
    }
  }

  /**
   * Re-renders the row for a newly selected colorway on the parent product,
   * using that color's pre-resolved counterpart — or hides the row if no
   * counterpart is sold in that colorway.
   * @param {string} color
   */
  renderForColor(color) {
    if (!this.#catalog?.colors) return;
    const entry = this.#catalog.colors[color];

    if (!entry || !entry.handle || !entry.variantId) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    if (this.refs.title) this.refs.title.textContent = `${entry.title}, ${color}`;

    const url = `${entry.url}?variant=${entry.variantId}`;
    const links = /** @type {HTMLAnchorElement[]} */ (Array.isArray(this.refs.link) ? this.refs.link : [this.refs.link].filter(Boolean));
    for (const link of links) {
      link.setAttribute('href', url);
    }

    if (this.refs.thumb instanceof HTMLImageElement && entry.thumb) {
      this.refs.thumb.src = entry.thumb;
      this.refs.thumb.alt = `${entry.title} in ${color}`;
    }

    const price = this.querySelector('[ref="price"]');
    if (price && entry.price) price.textContent = entry.price;
  }
}

if (!customElements.get('offsure-matching-piece')) {
  customElements.define('offsure-matching-piece', OffsureMatchingPiece);
}
